import { buildDefaultState, normalizeState } from '../../../core/persistence'
import type {
  PersistedState,
  AlbumConfig,
  AlbumRecord,
  InvestmentDestination,
  StickerInvestment
} from '../../../core/persistence'

export const CLOUD_ALBUMS_TABLE = 'albums'
export const CLOUD_DESTINATIONS_TABLE = 'investment_destinations'
export const CLOUD_STICKERS_TABLE = 'sticker_investments'
export const CLOUD_PREFERENCES_TABLE = 'user_preferences'
export const LEGACY_STATE_TABLE = 'user_state'

export interface CloudAlbumRow {
  id: string
  user_id: string
  name: string
  size: number
  theme_key: AlbumConfig['theme']
  currency: string
  target_finish_date: string | null
  created_at: string
  updated_at: string
}

export interface CloudDestinationRow {
  id: string
  album_id: string
  name: string
  kind: string
  notes: string
  created_at: string
  updated_at: string
}

export interface CloudStickerInvestmentRow {
  album_id: string
  sticker_number: number
  destination_id: string
  comments: string
  invested_at: string
}

export interface CloudUserPreferencesRow {
  user_id: string
  active_album_id: string | null
  locale: string | null
  theme_mode: string | null
  album_page_size: number | null
  legal_accepted: boolean | null
  updated_at: string
}

export interface NormalizedCloudPayload {
  albums: CloudAlbumRow[]
  destinations: CloudDestinationRow[]
  stickerInvestments: CloudStickerInvestmentRow[]
  preferences: CloudUserPreferencesRow
}

export function isEmptyNormalizedPayload(payload: {
  albums: CloudAlbumRow[]
  preferences: CloudUserPreferencesRow | null
}): boolean {
  return payload.albums.length === 0 && payload.preferences === null
}

export function serializePersistedStateToNormalizedCloud(
  state: PersistedState,
  userId: string
): NormalizedCloudPayload {
  const normalized = normalizeState(state)
  const now = new Date().toISOString()

  return {
    albums: normalized.albums.map((album) => ({
      id: album.config.id,
      user_id: userId,
      name: album.config.name,
      size: album.config.size,
      theme_key: album.config.theme,
      currency: album.config.currency,
      target_finish_date: album.config.targetFinishDate || null,
      created_at: album.config.createdAt,
      updated_at: album.config.updatedAt
    })),
    destinations: normalized.albums.flatMap((album) =>
      album.config.savedInvestments.map((destination) => ({
        id: destination.id,
        album_id: album.config.id,
        name: destination.name,
        kind: destination.kind,
        notes: destination.notes,
        created_at: album.config.createdAt,
        updated_at: album.config.updatedAt
      }))
    ),
    stickerInvestments: normalized.albums.flatMap((album) =>
      Object.values(album.stickers).map((investment) => ({
        album_id: album.config.id,
        sticker_number: investment.stickerNumber,
        destination_id: investment.destinationId,
        comments: investment.comments,
        invested_at: investment.investedAt
      }))
    ),
    preferences: {
      user_id: userId,
      active_album_id: normalized.activeAlbumId,
      locale: null,
      theme_mode: null,
      album_page_size: null,
      legal_accepted: null,
      updated_at: now
    }
  }
}

export function deserializePersistedStateFromNormalizedCloud(payload: {
  albums: CloudAlbumRow[]
  destinations: CloudDestinationRow[]
  stickerInvestments: CloudStickerInvestmentRow[]
  preferences: CloudUserPreferencesRow | null
}): PersistedState {
  if (payload.albums.length === 0) {
    return buildDefaultState()
  }

  const destinationsByAlbumId = new Map<string, InvestmentDestination[]>()
  for (const destination of payload.destinations) {
    const existing = destinationsByAlbumId.get(destination.album_id) ?? []
    existing.push({
      id: destination.id,
      name: destination.name,
      kind: destination.kind,
      notes: destination.notes
    })
    destinationsByAlbumId.set(destination.album_id, existing)
  }

  const stickersByAlbumId = new Map<string, Record<number, StickerInvestment>>()
  for (const sticker of payload.stickerInvestments) {
    const existing = stickersByAlbumId.get(sticker.album_id) ?? {}
    existing[sticker.sticker_number] = {
      stickerNumber: sticker.sticker_number,
      albumId: sticker.album_id,
      destinationId: sticker.destination_id,
      comments: sticker.comments,
      investedAt: sticker.invested_at
    }
    stickersByAlbumId.set(sticker.album_id, existing)
  }

  const albums: AlbumRecord[] = payload.albums.map((album) => ({
    config: {
      id: album.id,
      name: album.name,
      size: album.size,
      theme: album.theme_key,
      currency: album.currency,
      targetFinishDate: album.target_finish_date ?? '',
      savedInvestments: destinationsByAlbumId.get(album.id) ?? [],
      createdAt: album.created_at,
      updatedAt: album.updated_at
    },
    stickers: stickersByAlbumId.get(album.id) ?? {}
  }))

  return normalizeState({
    albums,
    activeAlbumId: payload.preferences?.active_album_id ?? null
  })
}
